import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { ShieldCheck, ImageIcon, Ban, DollarSign, Coins, CalendarDays } from 'lucide-react';

const fmtUsd = (n) => `$${Number(n || 0).toFixed(4)}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();

export default function ModerationStatsPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get('/admin/moderation-stats').then(r => setData(r.data)).catch(() => setError(true));
  }, []);

  const tiles = data ? [
    { key: 'images_run', label: 'Images checked', value: fmtNum(data.images_run), Icon: ImageIcon, color: '#2C5545' },
    { key: 'images_blocked', label: 'Images blocked', value: fmtNum(data.images_blocked), Icon: Ban, color: '#9A4A4A' },
    { key: 'images_allowed', label: 'Images allowed', value: fmtNum(data.images_allowed), Icon: ShieldCheck, color: '#7A9A78' },
    { key: 'est_cost_usd', label: 'Estimated total cost', value: fmtUsd(data.est_cost_usd), Icon: DollarSign, color: '#D99E4A' },
    { key: 'est_cost_per_image_usd', label: 'Est. cost / image', value: fmtUsd(data.est_cost_per_image_usd), Icon: Coins, color: '#7A6FC8' },
    { key: 'run_today', label: 'Checked today', value: `${fmtNum(data.run_today)} (${fmtNum(data.blocked_today)} blocked)`, Icon: CalendarDays, color: '#8A8F87' },
  ] : [];

  return (
    <div data-testid="moderation-stats-panel">
      <h2 className="font-display font-bold text-2xl text-brand-ink mb-1">Image Moderation</h2>
      <p className="text-brand-slate text-sm mb-6">
        Every uploaded photo is screened by {data?.model || 'gpt-4o-mini'}. Cost is an estimate based on public model pricing.
      </p>

      {error && <div className="glass-panel rounded-3xl p-6 text-brand-slate" data-testid="moderation-stats-error">Could not load moderation stats.</div>}

      {!error && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {tiles.map(({ key, label, value, Icon, color }) => (
              <div key={key} data-testid={`mod-stat-${key}`} className="glass-panel rounded-3xl p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: color + '22' }}>
                  <Icon className="w-6 h-6" style={{ color }} />
                </div>
                <div>
                  <div className="font-display font-black text-2xl text-brand-ink leading-none">{data ? value : '—'}</div>
                  <div className="text-xs text-brand-slate mt-1">{label}</div>
                </div>
              </div>
            ))}
          </div>

          {data?.by_category?.length > 0 && (
            <div className="mt-6 glass-panel rounded-3xl p-5" data-testid="mod-category-breakdown">
              <h3 className="font-display font-bold text-brand-ink mb-3">Blocked by reason</h3>
              <div className="flex flex-wrap gap-2">
                {data.by_category.map((c) => (
                  <span key={c.category} data-testid={`mod-cat-${c.category}`} className="inline-flex items-center gap-2 rounded-full bg-[#9A4A4A]/10 text-[#9A4A4A] px-3 py-1.5 text-sm font-medium">
                    {c.category}<span className="font-bold">{c.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {data && (
            <p className="mt-4 text-xs text-brand-dim">
              ≈ {fmtNum(data.input_tokens)} input + {fmtNum(data.output_tokens)} output tokens used. Estimated cost only — actual Universal Key credits may differ.
            </p>
          )}
        </>
      )}
    </div>
  );
}
